import React, { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { CollectionPropertyValue } from '../RealtimeCollectionProvider';
import { CollectionProperty, UrlUtils } from '@saga/shared';
import Button from '../styled/Button';
import { Edit2, Copy, Check } from 'react-feather';

const MAX_URL_LENGTH = 2048; // Common browser URL length limit

function CollectionPropertyURLSelect({
    property,
    onChangeValue,
    showEmptyState = false,
    disabled = false,
}: {
    property: CollectionPropertyValue & { type: CollectionProperty['type'] };
    onChangeValue: (value: string | undefined) => void;
    showEmptyState?: boolean;
    disabled?: boolean;
}) {
    const [isEditing, setIsEditing] = useState(false);
    const [value, setValue] = useState(property.value ?? '');
    const [isInvalid, setIsInvalid] = useState(false);
    const [copied, setCopied] = useState(false);
    const inputRef = useRef<HTMLInputElement>(null);
    const hasValue = value.trim() !== '';
    const { t } = useTranslation();

    useEffect(() => {
        setValue(property.value ?? '');
    }, [property.value]);

    useEffect(() => {
        if (isEditing && inputRef.current) {
            const input = inputRef.current;
            setTimeout(() => {
                input.focus();
                const length = input.value.length;
                input.setSelectionRange(length, length);
            }, 0);
            setIsInvalid(false);
        }
    }, [isEditing]);
    const formatURL = (url: string): string => {
        if (url.trim() === '') return '';
        // Check for dangerous protocols
        const dangerousProtocols = /^(javascript:|data:|vbscript:|file:|about:|blob:)/i;
        if (dangerousProtocols.test(url)) {
            return '';
        }
        return UrlUtils.addHttps(url);
    };
    const isValidURL = (url: string): boolean => {
        if (!url || url.trim() === '') return true;

        // Check URL length
        if (url.length > MAX_URL_LENGTH) return false;

        try {
            const urlObj = new URL(url);
            return ['http:', 'https:'].includes(urlObj.protocol);
        } catch {
            return false;
        }
    };
    const displayURL = (url: string): string => {
        return url.replace(/^https?:\/\//i, '');
    };
    const handleSave = () => {
        if (disabled) return;
        const formattedValue = formatURL(value);
        if (!isValidURL(formattedValue)) {
            setValue(property.value ?? '');
            setIsEditing(false);
            return;
        }
        onChangeValue(formattedValue.trim() === '' ? undefined : formattedValue);
        setIsEditing(false);
    };

    const handleCopy = async () => {
        if (!property.value) return;
        try {
            await navigator.clipboard.writeText(formatURL(property.value));
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
        } catch (err) {
            console.error('Failed to copy URL:', err);
        }
    };
    if (isEditing && !disabled) {
        return (
            <div className="w-40 min-h-6">
                <input
                    ref={inputRef}
                    type="url"
                    value={value}
                    maxLength={MAX_URL_LENGTH}
                    className={`min-h-6 h-6 text-sm bg-transparent focus:outline-none px-1 w-full leading-normal ${isInvalid ? 'text-red-500' : ''}`}
                    onChange={(e) => {
                        setValue(e.target.value);
                        const testValue = e.target.value.trim() ? formatURL(e.target.value) : '';
                        setIsInvalid(!!testValue && !isValidURL(testValue));
                    }}
                    onBlur={handleSave}
                    onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                            e.preventDefault();
                            handleSave();
                        } else if (e.key === 'Escape') {
                            setValue(property.value ?? '');
                            setIsEditing(false);
                        }
                    }}
                    placeholder={t('collections.properties.enter_url_placeholder', 'Enter URL')}
                />
            </div>
        );
    }
    if (hasValue) {
        return (
            <div
                className="group relative flex items-center w-40 min-h-6 cursor-pointer overflow-hidden"
                onClick={() => setIsEditing(true)}
            >
                <a
                    href={formatURL(value)}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center min-h-6 px-1 rounded hover:bg-saga-gray-200 dark:hover:bg-zinc-200/10 text-blue-600 dark:text-blue-400 hover:underline max-w-full"
                    onClick={(e) => e.stopPropagation()}
                    data-testid="property-url-link"
                >
                    <span className="text-sm truncate">{displayURL(value)}</span>
                </a>
                <div className="absolute top-[1px] right-0 flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                    <button
                        onClick={(e) => {
                            e.stopPropagation();
                            handleCopy();
                        }}
                        className="p-1 rounded bg-white dark:bg-zinc-700 shadow-sm hover:bg-saga-gray-200 dark:hover:bg-zinc-600 disabled:opacity-50 disabled:cursor-not-allowed"
                        aria-label={t('common.copy', 'Copy')}
                        disabled={disabled}
                    >
                        {copied ? (
                            <Check size={14} className="text-green-500" />
                        ) : (
                            <Copy size={14} className="text-saga-gray-500" />
                        )}
                    </button>
                    <button
                        onClick={(e) => {
                            e.stopPropagation();
                            setIsEditing(true);
                        }}
                        className="p-1 rounded bg-white dark:bg-zinc-700 shadow-sm hover:bg-saga-gray-200 dark:hover:bg-zinc-600 disabled:opacity-50 disabled:cursor-not-allowed"
                        aria-label={t('common.edit', 'Edit')}
                        disabled={disabled}
                    >
                        <Edit2 size={14} className="text-saga-gray-500" />
                    </button>
                </div>
            </div>
        );
    }

    if (showEmptyState) {
        return (
            <Button.DashedPill
                onClick={() => !disabled && setIsEditing(true)}
                data-testid="property-input-empty-url"
                disabled={disabled}
            >
                <div className="flex space-x-1 items-center h-5">
                    <span className="whitespace-nowrap">{t('collections.properties.add_url', 'Add URL')}</span>
                </div>
            </Button.DashedPill>
        );
    }
    return null;
}

export default CollectionPropertyURLSelect;
